import React, { useEffect, useState } from 'react';
import { FaBriefcaseMedical, FaEdit, FaPlus, FaTrash, FaToggleOff, FaToggleOn } from 'react-icons/fa';
import apiClient from '../../utils/axiosPetition';
import Swal from 'sweetalert2';

const DEFAULT_GROUPS = ['Médicos', 'Terapias', 'Otros Profesionales'];

function formatDate(date) {
  if (!date) return '—';
  const d = new Date(date);
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const año = d.getFullYear();
  const hora = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dia}/${mes}/${año} ${hora}:${min}`;
}

const emptyForm = {
  label: '',
  value: '',
  group: DEFAULT_GROUPS[0],
  order: 0,
};

const GestionProfesiones = () => {
  const [professions, setProfessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProfessions();
  }, []);

  const fetchProfessions = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/professions/gestion');
      setProfessions(Array.isArray(res.data) ? res.data : []);
    } catch {
      Swal.fire('Error', 'No se pudieron cargar las profesiones.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditItem(null);
    setForm(emptyForm);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      label: item.label,
      value: item.value,
      group: item.group,
      order: item.order ?? 0,
    });
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditItem(null);
    setForm(emptyForm);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'order' ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) {
      setError('El nombre visible es obligatorio');
      return;
    }
    if (!form.group.trim()) {
      setError('La categoría es obligatoria');
      return;
    }

    const payload = {
      label: form.label.trim(),
      group: form.group.trim(),
      order: Number(form.order) || 0,
      value: form.value.trim() || form.label.trim(),
    };

    try {
      if (editItem) {
        const res = await apiClient.put(`/api/professions/${editItem._id}`, payload);
        setProfessions((prev) => prev.map((p) => (p._id === editItem._id ? res.data : p)));
        Swal.fire('Actualizado', 'La profesión fue actualizada.', 'success');
      } else {
        const res = await apiClient.post('/api/professions', payload);
        setProfessions((prev) => [...prev, res.data].sort((a, b) => {
          if (a.group !== b.group) return a.group.localeCompare(b.group);
          return (a.order || 0) - (b.order || 0);
        }));
        Swal.fire('Creada', 'La profesión fue agregada al listado de registro.', 'success');
      }
      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo guardar la profesión');
    }
  };

  const handleToggle = async (item) => {
    const action = item.active ? 'desactivar' : 'activar';
    const confirm = await Swal.fire({
      title: item.active ? '¿Desactivar profesión?' : '¿Activar profesión?',
      text: item.active
        ? 'Dejará de aparecer en el registro de nuevos profesionales.'
        : 'Volverá a estar disponible en el registro.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: item.active ? 'Desactivar' : 'Activar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.isConfirmed) return;

    try {
      const res = await apiClient.patch(`/api/professions/${item._id}/estado`, {
        active: !item.active,
      });
      setProfessions((prev) => prev.map((p) => (p._id === item._id ? res.data : p)));
      Swal.fire('Listo', `Profesión ${action === 'activar' ? 'activada' : 'desactivada'}.`, 'success');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || `No se pudo ${action} la profesión.`, 'error');
    }
  };

  const handleDelete = async (item) => {
    const confirm = await Swal.fire({
      title: '¿Eliminar profesión?',
      text: 'Solo se puede eliminar si ningún profesional la tiene asignada.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
    });
    if (!confirm.isConfirmed) return;

    try {
      await apiClient.delete(`/api/professions/${item._id}`);
      setProfessions((prev) => prev.filter((p) => p._id !== item._id));
      Swal.fire('Eliminada', 'La profesión fue eliminada.', 'success');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'No se pudo eliminar la profesión.', 'error');
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gradient-to-r from-[#205087] to-[#1f94c2]">
          <div className="flex items-center gap-3 text-white">
            <FaBriefcaseMedical className="text-2xl shrink-0" />
            <div>
              <h1 className="text-xl font-bold">Profesiones</h1>
              <p className="text-sm text-white/85 mt-0.5">
                Listado desplegable al registrar profesionales de la salud
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white text-[#205087] font-semibold text-sm hover:bg-gray-50 transition-colors"
          >
            <FaPlus />
            Nueva profesión
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <p className="text-center text-gray-500 py-10">Cargando profesiones...</p>
          ) : professions.length === 0 ? (
            <p className="text-center text-gray-500 py-10">No hay profesiones registradas.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#205087] text-white">
                    <th className="text-left py-3 px-4 font-semibold">Nombre visible</th>
                    <th className="text-left py-3 px-4 font-semibold">Identificador</th>
                    <th className="text-left py-3 px-4 font-semibold">Categoría</th>
                    <th className="text-center py-3 px-4 font-semibold">Orden</th>
                    <th className="text-center py-3 px-4 font-semibold">Estado</th>
                    <th className="text-left py-3 px-4 font-semibold">Última edición</th>
                    <th className="text-center py-3 px-4 font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {professions.map((item) => (
                    <tr key={item._id} className="border-t border-gray-100 hover:bg-gray-50/80">
                      <td className="py-3 px-4 font-medium text-gray-900">{item.label}</td>
                      <td className="py-3 px-4 text-gray-600 font-mono text-xs">{item.value}</td>
                      <td className="py-3 px-4 text-gray-700">{item.group}</td>
                      <td className="py-3 px-4 text-center text-gray-600">{item.order ?? 0}</td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                            item.active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {item.active ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{formatDate(item.ultimaEdicion)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(item)}
                            className="p-2 rounded-lg text-[#1f94c2] hover:bg-blue-50"
                            title="Editar"
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggle(item)}
                            className={`p-2 rounded-lg ${item.active ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                            title={item.active ? 'Desactivar' : 'Activar'}
                          >
                            {item.active ? <FaToggleOn /> : <FaToggleOff />}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item)}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                            title="Eliminar"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-4 text-xs text-gray-500">
            El identificador interno se guarda en el perfil del profesional. Si ya hay profesionales
            registrados con esa profesión, no podrá cambiarse ni desactivarse hasta que ninguno la use.
          </p>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-[#205087]">
                {editItem ? 'Editar profesión' : 'Nueva profesión'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre visible *</label>
                <input
                  type="text"
                  name="label"
                  value={form.label}
                  onChange={handleChange}
                  placeholder="Ej. Optometría"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1f94c2]/30 focus:border-[#1f94c2]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Identificador interno</label>
                <input
                  type="text"
                  name="value"
                  value={form.value}
                  onChange={handleChange}
                  placeholder="Se genera del nombre si se deja vacío"
                  disabled={Boolean(editItem)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1f94c2]/30 focus:border-[#1f94c2] disabled:bg-gray-100 disabled:text-gray-500"
                />
                {editItem && (
                  <p className="text-xs text-gray-500 mt-1">
                    No editable si hay profesionales que ya la usan.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría *</label>
                <select
                  name="group"
                  value={form.group}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#1f94c2]/30 focus:border-[#1f94c2]"
                >
                  {(form.group && !DEFAULT_GROUPS.includes(form.group)
                    ? [form.group, ...DEFAULT_GROUPS]
                    : DEFAULT_GROUPS
                  ).map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Orden en la lista</label>
                <input
                  type="number"
                  name="order"
                  min="0"
                  value={form.order}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1f94c2]/30 focus:border-[#1f94c2]"
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 rounded-xl bg-[#1f94c2] text-white font-semibold hover:bg-[#187aa3]"
                >
                  {editItem ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionProfesiones;
